"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import { ArrowRight, Search, Sprout, TrendingUp, GraduationCap, LayoutGrid } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { CategoryIcon } from "@/components/academy/category-icon";
import { LevelBadge } from "@/components/academy/level-badge";
import { StockLogo } from "@/components/stock-logo";
import type { AcademyArticle } from "@/lib/academy/articles";
import { cn } from "@/lib/utils";

const LEVELS = ["Einsteiger", "Fortgeschritten", "Experte"] as const;

/** Icon + Kurzbeschreibung je Stufe fuer den grossen Stufen-Wähler (Bug-Fix/Redesign,
 * Erik 2026-08-20: "aktuell ist es zu versteckt und wird direkt übersehen") - dieselbe
 * 3-Ton-Farblogik wie LevelBadge (gruen/Gold/Navy), hier nur grossflaechiger. */
const LEVEL_INFO: Record<(typeof LEVELS)[number], { icon: typeof Sprout; description: string }> = {
  Einsteiger: { icon: Sprout, description: "Keine Vorkenntnisse nötig - hier ansetzen." },
  Fortgeschritten: { icon: TrendingUp, description: "Grundlagen sitzen, jetzt tiefer einsteigen." },
  Experte: { icon: GraduationCap, description: "Fortgeschrittene Modelle und Kennzahlen." },
};

const LEVEL_CARD_STYLES: Record<(typeof LEVELS)[number], { activeClass: string; iconClass: string }> = {
  Einsteiger: { activeClass: "border-positive bg-positive/10", iconClass: "bg-positive/15 text-positive" },
  Fortgeschritten: { activeClass: "border-gold bg-gold/10", iconClass: "bg-gold/20 text-gold-text" },
  Experte: { activeClass: "border-primary bg-primary/10", iconClass: "bg-primary/15 text-primary" },
};

function categoryAnchor(category: string): string {
  return category
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

/**
 * Durchsuchbare Akademie-Übersicht (Erik, Runde 8 Punkt 4: "durchsuchbare
 * Übersichtsseite" statt nur der bisherigen reinen Kategorien-Gruppierung) - filtert
 * Titel + Kurzbeschreibung client-seitig, kein Server-Request. Eigene kleine
 * Client-Komponente statt die ganze Seite "use client" zu machen - der Rest bleibt
 * ein einfacher Server Component.
 *
 * Erik-Feedback 2026-07-22 ("deutlich strukturierter, moderner und aufgeräumter... muss
 * ersichtlich sein, dass man da raufklicken kann"): neue Kategorie-Übersichtszeile oben
 * (Icon + Name + Artikelanzahl, springt per Anker zum jeweiligen Abschnitt) macht die
 * vorhandene Struktur auf einen Blick sichtbar, plus deutlicherer Hover-/Klick-Hinweis
 * auf jeder Artikel-Karte (Pfeil-Icon, das beim Hover nach rechts wandert, Rahmenfarbe).
 *
 * 2026-08-19-Großausbau: zusätzlicher Stufen-Filter (Einsteiger/Fortgeschritten/Experte,
 * backlog.md -> [Punkt 34]) neben der Textsuche - beide Filter kombinieren sich (UND-
 * Verknüpfung), nicht exklusiv zueinander.
 *
 * **2026-08-20 als grosse Karten statt kleiner Pillen neu gestaltet** (Erik: "aktuell ist
 * es zu versteckt und wird direkt übersehen") - eigene Ueberschrift ("Wo möchtest du
 * einsteigen?"), bewusst VOR der Textsuche platziert, damit die Stufenwahl die erste
 * wahrgenommene Interaktion auf der Seite ist statt einer Detail-Einstellung daneben.
 * Gleiche Farblogik/gleicher State wie zuvor, nur visuell deutlich groesser/eigenstaendiger.
 */
export function AcademySearch({
  categories,
  articles,
}: {
  categories: readonly string[];
  articles: AcademyArticle[];
}) {
  const [query, setQuery] = useState("");
  const [level, setLevel] = useState<(typeof LEVELS)[number] | null>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return articles.filter((a) => {
      const matchesQuery = !q || a.title.toLowerCase().includes(q) || a.summary.toLowerCase().includes(q);
      const matchesLevel = !level || a.level === level;
      return matchesQuery && matchesLevel;
    });
  }, [articles, query, level]);

  const countByCategory = useMemo(() => {
    const map = new Map<string, number>();
    for (const a of articles) map.set(a.category, (map.get(a.category) ?? 0) + 1);
    return map;
  }, [articles]);

  return (
    <div className="flex flex-col gap-10">
      {/* Grosser Stufen-Waehler (Redesign, Erik 2026-08-20: "deutlich übersichtlicher,
          dass man direkt auswählen kann, ob man Einsteiger, Fortgeschritten oder Experte
          ist... aktuell ist es zu versteckt") - ersetzt die vorherigen kleinen Pillen
          durch 4 grosse, klar beschriftete Karten (3 Stufen + "Alle"), bewusst VOR der
          Textsuche als erste Interaktion auf der Seite. */}
      <div className="flex flex-col gap-3">
        <h2 className="font-serif text-lg text-ink">Wo möchtest du einsteigen?</h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" role="group" aria-label="Nach Schwierigkeitsstufe filtern">
          <button
            type="button"
            onClick={() => setLevel(null)}
            aria-pressed={level === null}
            className={cn(
              "flex flex-col items-start gap-2 rounded-xl border-2 p-4 text-left transition-colors",
              level === null ? "border-primary bg-primary/[0.04]" : "border-hairline bg-card hover:border-secondary/40",
            )}
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-ink">
              <LayoutGrid className="h-5 w-5" aria-hidden="true" />
            </span>
            <span className="font-serif text-base text-ink">Alle Stufen</span>
            <span className="text-xs text-muted-foreground">Den ganzen Katalog durchstöbern.</span>
          </button>
          {LEVELS.map((l) => {
            const { icon: Icon, description } = LEVEL_INFO[l];
            const { activeClass, iconClass } = LEVEL_CARD_STYLES[l];
            const isActive = level === l;
            return (
              <button
                key={l}
                type="button"
                onClick={() => setLevel((current) => (current === l ? null : l))}
                aria-pressed={isActive}
                className={cn(
                  "flex flex-col items-start gap-2 rounded-xl border-2 p-4 text-left transition-colors",
                  isActive ? activeClass : "border-hairline bg-card hover:border-secondary/40",
                )}
              >
                <span className={cn("flex h-10 w-10 items-center justify-center rounded-full", iconClass)}>
                  <Icon className="h-5 w-5" aria-hidden="true" />
                </span>
                <span className="font-serif text-base text-ink">{l}</span>
                <span className="text-xs text-muted-foreground">{description}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <div className="relative max-w-sm">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Artikel durchsuchen…"
            className="bg-card pl-9"
            aria-label="Akademie-Artikel durchsuchen"
          />
        </div>

        {/* Kategorie-Übersicht: macht auf einen Blick sichtbar, welche Themenbereiche es
            gibt, bevor man in der langen Artikelliste scrollt - springt per Anker-Link
            zum jeweiligen Abschnitt. */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {categories.map((category) => (
            <a
              key={category}
              href={`#${categoryAnchor(category)}`}
              className="group flex items-center gap-3 rounded-lg border border-hairline bg-card p-3 transition-colors hover:border-secondary/40 hover:bg-muted/40"
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted text-ink transition-colors group-hover:bg-secondary/15 group-hover:text-secondary">
                <CategoryIcon category={category} className="h-5 w-5" />
              </span>
              <span className="min-w-0">
                <span className="block truncate text-sm font-medium text-ink">{category}</span>
                <span className="block text-xs text-muted-foreground">
                  {countByCategory.get(category) ?? 0} Artikel
                </span>
              </span>
            </a>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground">Keine Artikel gefunden für „{query}“.</p>
      ) : (
        categories.map((category) => {
          const categoryArticles = filtered.filter((a) => a.category === category);
          if (categoryArticles.length === 0) return null;
          return (
            <section key={category} id={categoryAnchor(category)} className="flex scroll-mt-24 flex-col gap-3">
              <h2 className="flex items-center gap-2 font-serif text-xl text-ink">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-secondary">
                  <CategoryIcon category={category} className="h-4 w-4" />
                </span>
                {category}
              </h2>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {categoryArticles.map((article) => (
                  <Link key={article.slug} href={`/akademie/${article.slug}`}>
                    <Card
                      className={cn(
                        "reveal group h-full border-hairline transition-colors hover:border-secondary/40 hover:bg-muted/40",
                      )}
                    >
                      <CardContent className="flex h-full flex-col gap-1.5 pt-6">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="font-serif text-lg font-semibold text-ink">{article.title}</h3>
                          <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-secondary" />
                        </div>
                        <LevelBadge level={article.level} />
                        <p className="text-sm text-muted-foreground">{article.summary}</p>
                        {article.exampleTickers && article.exampleTickers.length > 0 && (
                          <div className="mt-auto flex items-center gap-1 pt-1">
                            {article.exampleTickers.slice(0, 3).map((symbol) => (
                              <StockLogo key={symbol} symbol={symbol} size={20} bordered={false} />
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </section>
          );
        })
      )}
    </div>
  );
}
